import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import BugReportIcon from "@mui/icons-material/BugReport";
import HubIcon from "@mui/icons-material/Hub";
import LanguageIcon from "@mui/icons-material/Language";
import SecurityIcon from "@mui/icons-material/Security";
import DnsIcon from "@mui/icons-material/Dns";
import MemoryIcon from "@mui/icons-material/Memory";
import TerminalIcon from "@mui/icons-material/Terminal";

import ForceGraph2D from "react-force-graph-2d";
import { forceCollide } from "d3-force";

import Nav from "./nav";
import { BASEURL, PRIMARYCOLOR } from "../../Constants";

// ----------------------------
// Helpers
// ----------------------------
const BRAND = PRIMARYCOLOR || "#005CC5";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const safeStr = (v) => String(v ?? "").trim();

const safeParseList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim())
      .filter(Boolean)
      .map((v) => v.replace(/:+$/, ""));
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => String(v).trim())
            .filter(Boolean)
            .map((v) => v.replace(/:+$/, ""));
        }
      } catch {
        // fall through
      }
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((v) => v.replace(/:+$/, ""));
    }

    return [trimmed.replace(/:+$/, "")].filter(Boolean);
  }

  return [];
};

function normalizeSeverity(sev) {
  const s = safeStr(sev).toLowerCase();
  if (!s) return "info";
  if (s.includes("crit")) return "critical";
  if (s.includes("high")) return "high";
  if (s.includes("med")) return "medium";
  if (s.includes("low")) return "low";
  return "info";
}

function safeUrlParse(u) {
  try {
    const url = new URL(u);
    return {
      ok: true,
      host: url.hostname,
      path: url.pathname || "/",
      full: url.toString(),
      port: url.port || "",
      proto: url.protocol.replace(":", ""),
    };
  } catch {
    return { ok: false };
  }
}

function splitHostPath(token) {
  // supports: host:port/path OR host/path OR host:port
  const s = safeStr(token);
  if (!s) return { host: "", path: "" };

  // already has protocol
  if (s.includes("://")) {
    const parsed = safeUrlParse(s);
    if (parsed.ok) return { host: parsed.host, path: parsed.path, full: parsed.full };
  }

  const slashIdx = s.indexOf("/");
  if (slashIdx !== -1) {
    const left = s.slice(0, slashIdx);
    const right = s.slice(slashIdx);
    // left may be host:port
    const host = left.split(":")[0];
    return { host: host || left, path: right || "/" };
  }

  // host:port only
  const host = s.split(":")[0];
  return { host: host || s, path: "" };
}

function uniqBy(arr, fnKey) {
  const seen = new Set();
  const out = [];
  for (const x of arr) {
    const k = fnKey(x);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(x);
  }
  return out;
}

// Canvas round rect helper
function drawRoundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

// ----------------------------
// Node type styling (cyber palette)
// ----------------------------
const TYPE = {
  ASSET: "asset",
  PORT: "port",
  SERVICE: "service",
  TECH: "tech",
  URL: "url",
  THREAT: "threat",
  FINDING: "finding",
};

// ✅ More obvious type palette per your request
const COLORS = {
  ASSET: "rgba(0, 92, 197, 1)", // blue
  PURPLE: "rgba(165, 80, 255, 0.98)", // tech/port/service
  URL: "rgba(0, 190, 110, 0.98)", // green
  THREAT: "rgba(255, 150, 35, 0.98)", // orange
};

// Findings act as exploits now — severity-based red family
const findingColorBySeverity = (sev) => {
  const s = normalizeSeverity(sev);
  if (s === "critical") return "rgba(255, 40, 70, 1)";
  if (s === "high") return "rgba(255, 60, 90, 0.98)";
  if (s === "medium") return "rgba(255, 100, 60, 0.98)";
  if (s === "low") return "rgba(255, 150, 80, 0.95)";
  return "rgba(255, 80, 110, 0.92)";
};

const typeColor = (node) => {
  const t = node?.type;
  if (t === TYPE.ASSET) return COLORS.ASSET;
  if (t === TYPE.PORT) return COLORS.PURPLE;
  if (t === TYPE.SERVICE) return COLORS.PURPLE;
  if (t === TYPE.TECH) return COLORS.PURPLE;
  if (t === TYPE.URL) return COLORS.URL;
  if (t === TYPE.THREAT) return COLORS.THREAT;
  if (t === TYPE.FINDING) return findingColorBySeverity(node?.severity);

  return "rgba(180, 210, 255, 0.95)";
};

// ✅ Make assets feel like "root" nodes visually (BIGGER)
const typeRadius = (node) => {
  const t = node?.type;
  if (t === TYPE.ASSET) return 26; // ⬅️ MAIN ROOT NODE = MUCH BIGGER
  if (t === TYPE.FINDING) return 16;
  if (t === TYPE.THREAT) return 14;
  if (t === TYPE.URL) return 11;
  if (t === TYPE.TECH) return 10;
  if (t === TYPE.PORT) return 9;
  if (t === TYPE.SERVICE) return 9;
  return 9;
};

// Simple “icon glyph” per node type
const typeGlyph = (node) => {
  if (!node?.type) return "";
  if (node.type === TYPE.ASSET) return "◎";
  if (node.type === TYPE.FINDING) return "☠";
  if (node.type === TYPE.THREAT) return "⚠";
  if (node.type === TYPE.URL) return "🔗";
  if (node.type === TYPE.TECH) return "T";
  if (node.type === TYPE.PORT) return "P";
  if (node.type === TYPE.SERVICE) return "S";
  return "";
};

// shape draw helpers
function drawTriangle(ctx, x, y, r) {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r, y + r);
  ctx.lineTo(x - r, y + r);
  ctx.closePath();
}

function drawDiamond(ctx, x, y, r) {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r, y);
  ctx.closePath();
}

function drawRoundedSquare(ctx, x, y, r) {
  const size = r * 2;
  drawRoundRect(ctx, x - r, y - r, size, size, Math.max(5, r * 0.55));
}

// ----------------------------
// Page
// ----------------------------
export default function AttackGraph() {
  const { pentest_id } = useParams();
  const navigate = useNavigate();
  const fgRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [assets, setAssets] = useState([]);
  const [urls, setUrls] = useState([]);

  // UI state
  const [search, setSearch] = useState("");

  const [showPorts, setShowPorts] = useState(true);
  const [showServices, setShowServices] = useState(true);
  const [showTech, setShowTech] = useState(true);
  const [showUrls, setShowUrls] = useState(true);
  const [showThreatModels, setShowThreatModels] = useState(true);
  const [showThreatFindings, setShowThreatFindings] = useState(true);

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoverNode, setHoverNode] = useState(null);

  // ----------------------------
  // Fetch data (YOUR API CALLS)
  // ----------------------------
  useEffect(() => {
    let mounted = true;

    async function loadAll() {
      setLoading(true);
      setErr("");

      const token = sessionStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        };

        const [aRes, uRes] = await Promise.all([
          fetch(`${BASEURL}/pentest/assets?pentest_id=${encodeURIComponent(pentest_id || "")}`, { headers }),
          fetch(`${BASEURL}/pentest/urls?pentest_id=${encodeURIComponent(pentest_id || "")}`, { headers }),
        ]);

        const [aData, uData] = await Promise.all([aRes.json(), uRes.json()]);

        if (!mounted) return;

        if (aData?.error && aData?.message === "Invalid token") {
          navigate("/");
          return;
        }

        setAssets(Array.isArray(aData?.assets) ? aData.assets : []);
        setUrls(Array.isArray(uData?.urls) ? uData.urls : []);
        setLoading(false);

        setTimeout(() => {
          try {
            fgRef.current?.zoomToFit?.(900, 90);
          } catch {}
        }, 250);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Failed to load graph data");
        setLoading(false);
      }
    }

    loadAll();
    return () => {
      mounted = false;
    };
  }, [navigate, pentest_id]);

  // ----------------------------
  // Build raw graph (nodes + links)
  // ----------------------------
  const rawGraph = useMemo(() => {
    const nodesById = new Map();
    const links = [];
    const linkKeySet = new Set();

    const addNode = (node) => {
      if (!node?.id) return;
      if (nodesById.has(node.id)) {
        const existing = nodesById.get(node.id);
        nodesById.set(node.id, {
          ...existing,
          ...node,
          meta: { ...(existing.meta || {}), ...(node.meta || {}) },
        });
        return;
      }
      nodesById.set(node.id, node);
    };

    const addLink = (source, target, type = "rel") => {
      if (!source || !target) return;
      const k = `${source}=>${target}::${type}`;
      if (linkKeySet.has(k)) return;
      linkKeySet.add(k);
      links.push({ source, target, type });
    };

    // ✅ parse finding locations into tokens
    const parseFindingLocations = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.map((x) => safeStr(x)).filter(Boolean);

      const s = safeStr(raw);
      if (!s) return [];

      // json list style
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const jsonish = s
            .replace(/'/g, '"')
            .replace(/\bNone\b/g, "null")
            .replace(/\bTrue\b/g, "true")
            .replace(/\bFalse\b/g, "false");
          const parsed = JSON.parse(jsonish);
          if (Array.isArray(parsed)) return parsed.map((x) => safeStr(x)).filter(Boolean);
        } catch {
          const inner = s.slice(1, -1);
          return inner
            .split(",")
            .map((x) => x.trim().replace(/^['"]|['"]$/g, ""))
            .filter(Boolean);
        }
      }

      // csv
      if (s.includes(",")) {
        return s
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      }

      return [s];
    };

    // --- Assets, ports, services, tech ---
    for (const a of assets) {
      const host = safeStr(a?.host);
      if (!host) continue;

      const assetId = `asset:${host}`;
      addNode({
        id: assetId,
        type: TYPE.ASSET,
        label: host,
        meta: {
          host,
          description: safeStr(a?.description),
          ports: safeParseList(a?.ports),
          services: safeParseList(a?.services),
          technologies: safeParseList(a?.technologies),
          cpes: safeParseList(a?.cpes),
        },
      });

      // ports
      for (const p of safeParseList(a?.ports)) {
        const port = safeStr(p);
        if (!port) continue;
        const id = `port:${port}`;
        addNode({ id, type: TYPE.PORT, label: `:${port}`, meta: { port } });
        addLink(assetId, id, "port");
      }

      // services
      for (const s of safeParseList(a?.services)) {
        const svc = safeStr(s);
        if (!svc) continue;
        const id = `service:${svc.toLowerCase()}`;
        addNode({ id, type: TYPE.SERVICE, label: svc, meta: { service: svc } });
        addLink(assetId, id, "service");
      }

      // technologies
      for (const t of safeParseList(a?.technologies)) {
        const tech = safeStr(t);
        if (!tech) continue;
        const id = `tech:${tech}`;
        addNode({ id, type: TYPE.TECH, label: tech, meta: { tech } });
        addLink(assetId, id, "tech");
      }

      // --- threat models / attack_score ---
      const threatModel = a?.threat_model;
      const scores = safeArray(threatModel?.attack_score);

      for (const attack of scores) {
        const attackName = safeStr(attack?.name) || "Threat Model";
        const scoreVal = safeStr(attack?.score);
        const vulnerable = !!attack?.exploit_results?.vulnerable;

        const tmId = `tm:${host}:${attackName}`;
        addNode({
          id: tmId,
          type: TYPE.THREAT,
          label: `${attackName}${scoreVal ? ` (${scoreVal})` : ""}`,
          meta: {
            host,
            attackName,
            score: scoreVal,
            services: safeArray(attack?.service || attack?.services)
              .map((x) => safeStr(x))
              .filter(Boolean),
            ports: safeArray(attack?.ports).map((x) => safeStr(x)).filter(Boolean),
            vulnerable,
            reason: safeStr(attack?.reason),
          },
        });

        addLink(assetId, tmId, "threat");

        // ✅ ONLY keep these — these are now your only exploit/finding nodes
        const findings = safeArray(attack?.exploit_results?.findings);
        for (const f of findings) {
          const fname = safeStr(f?.name);
          if (!fname) continue;

          const fId = `finding:${host}:${attackName}:${fname}`;
          addNode({
            id: fId,
            type: TYPE.FINDING,
            label: fname,
            severity: normalizeSeverity(f?.severity),
            meta: {
              host,
              threat: attackName,
              name: fname,
              severity: normalizeSeverity(f?.severity),
              description: safeStr(f?.description),
              location: f?.location,
              remediation: safeStr(f?.remediation),
            },
          });

          // ✅ relevant threat model only (TM -> Finding)
          addLink(tmId, fId, "finding");

          // ✅ Finding -> URL/Page based on location (if it has a path)
          const locTokens = parseFindingLocations(f?.location);

          for (const token of locTokens) {
            const rawLoc = safeStr(token);
            if (!rawLoc) continue;

            // If it's just a path like "/admin"
            if (rawLoc.startsWith("/")) {
              const urlNodeId = `url:${host}${rawLoc}`;
              if (!nodesById.has(urlNodeId)) {
                addNode({
                  id: urlNodeId,
                  type: TYPE.URL,
                  label: rawLoc,
                  meta: { host, path: rawLoc, full: `${host}${rawLoc}` },
                });
              }
              addLink(fId, urlNodeId, "impacted_url");
              continue;
            }

            // Full URL
            let parsed = safeUrlParse(rawLoc);
            if (!parsed.ok && !rawLoc.includes("://")) {
              parsed = safeUrlParse("https://" + rawLoc);
            }

            if (parsed.ok) {
              const h = safeStr(parsed.host) || host;
              const p = safeStr(parsed.path) || "/";
              if (!p.startsWith("/")) continue;

              const assetNodeId = `asset:${h}`;
              if (!nodesById.has(assetNodeId)) {
                addNode({
                  id: assetNodeId,
                  type: TYPE.ASSET,
                  label: h,
                  meta: { host: h, description: "", ports: [], services: [], technologies: [], cpes: [] },
                });
              }

              const urlNodeId = `url:${h}${p}`;
              if (!nodesById.has(urlNodeId)) {
                addNode({
                  id: urlNodeId,
                  type: TYPE.URL,
                  label: p,
                  meta: { host: h, path: p, full: parsed.full || `${h}${p}` },
                });
              }

              // ensure URL links back to Asset
              addLink(assetNodeId, urlNodeId, "url");

              // finding impacts this specific page
              addLink(fId, urlNodeId, "impacted_url");
              continue;
            }

            // host/path style
            const hp = splitHostPath(rawLoc);
            const h = safeStr(hp.host) || host;
            const p = safeStr(hp.path);

            if (p && p.startsWith("/")) {
              const assetNodeId = `asset:${h}`;
              if (!nodesById.has(assetNodeId)) {
                addNode({
                  id: assetNodeId,
                  type: TYPE.ASSET,
                  label: h,
                  meta: { host: h, description: "", ports: [], services: [], technologies: [], cpes: [] },
                });
              }

              const urlNodeId = `url:${h}${p}`;
              if (!nodesById.has(urlNodeId)) {
                addNode({
                  id: urlNodeId,
                  type: TYPE.URL,
                  label: p,
                  meta: { host: h, path: p, full: `${h}${p}` },
                });
              }

              // ensure URL links back to Asset
              addLink(assetNodeId, urlNodeId, "url");

              // finding impacts this specific page
              addLink(fId, urlNodeId, "impacted_url");
            }
          }
        }
      }
    }

    // ✅ URL host extraction that always links back to asset:
    const extractUrlHostPath = (u) => {
      const rawFull = safeStr(u?.url);
      let host = safeStr(u?.host || u?.hostname || u?.domain || u?.asset || u?.target);
      let path = "";

      if (rawFull) {
        let parsed = safeUrlParse(rawFull);

        if (!parsed.ok && !rawFull.includes("://")) {
          parsed = safeUrlParse("https://" + rawFull);
        }

        if (parsed.ok) {
          host = parsed.host || host;
          path = parsed.path || "/";
          return { ok: true, host, path: path || "/", full: parsed.full || rawFull };
        }

        const hp = splitHostPath(rawFull.replace(/^\/\//, ""));
        if (!host) host = safeStr(hp.host);
        path = safeStr(hp.path) || "/";
      }

      if (!host) return { ok: false };
      if (!path) path = "/";
      return { ok: true, host, path, full: rawFull || `${host}${path}` };
    };

    // --- URLs ---
    for (const u of urls) {
      const extracted = extractUrlHostPath(u);
      if (!extracted.ok) continue;

      const host = extracted.host;
      const path = extracted.path || "/";
      const full = extracted.full || `${host}${path}`;

      const assetId = `asset:${host}`;
      if (!nodesById.has(assetId)) {
        addNode({
          id: assetId,
          type: TYPE.ASSET,
          label: host,
          meta: { host, description: "", ports: [], services: [], technologies: [], cpes: [] },
        });
      }

      const urlNodeId = `url:${host}${path}`;
      addNode({
        id: urlNodeId,
        type: TYPE.URL,
        label: path,
        meta: {
          host,
          path,
          full,
          title: u?.title,
          status: u?.status,
          content_length: u?.content_length,
        },
      });

      addLink(assetId, urlNodeId, "url");
    }

    const nodes = [...nodesById.values()];
    return { nodes, links };
  }, [assets, urls]);

  // ----------------------------
  // Filters + search reduce graph
  // ----------------------------
  const graph = useMemo(() => {
    const q = safeStr(search).toLowerCase();

    const typeEnabled = (n) => {
      if (n.type === TYPE.ASSET) return true;
      if (n.type === TYPE.PORT) return showPorts;
      if (n.type === TYPE.SERVICE) return showServices;
      if (n.type === TYPE.TECH) return showTech;
      if (n.type === TYPE.URL) return showUrls;
      if (n.type === TYPE.THREAT) return showThreatModels;
      if (n.type === TYPE.FINDING) return showThreatFindings;
      return true;
    };

    const nodes = rawGraph.nodes.filter((n) => typeEnabled(n));
    const nodesById = new Map(nodes.map((n) => [n.id, n]));

    const links = rawGraph.links
      .map((l) => {
        const s = typeof l.source === "object" ? l.source.id : l.source;
        const t = typeof l.target === "object" ? l.target.id : l.target;
        return { ...l, source: s, target: t };
      })
      .filter((l) => nodesById.has(l.source) && nodesById.has(l.target));

    const uniqLinks = uniqBy(links, (x) => `${x.source}=>${x.target}::${x.type}`);

    return { nodes, links: uniqLinks, q };
  }, [
    rawGraph,
    search,
    showPorts,
    showServices,
    showTech,
    showUrls,
    showThreatModels,
    showThreatFindings,
  ]);

  const counts = useMemo(() => {
    const c = {
      assets: 0,
      ports: 0,
      services: 0,
      tech: 0,
      urls: 0,
      threats: 0,
      findings: 0,
      links: graph.links.length,
    };

    for (const n of graph.nodes) {
      if (n.type === TYPE.ASSET) c.assets++;
      else if (n.type === TYPE.PORT) c.ports++;
      else if (n.type === TYPE.SERVICE) c.services++;
      else if (n.type === TYPE.TECH) c.tech++;
      else if (n.type === TYPE.URL) c.urls++;
      else if (n.type === TYPE.THREAT) c.threats++;
      else if (n.type === TYPE.FINDING) c.findings++;
    }

    return c;
  }, [graph]);

  // ----------------------------
  // Highlighting / selection helpers
  // ----------------------------
  const selectionInfo = useMemo(() => {
    if (!selectedNode) return { neighborIds: new Set(), neighborLinks: new Set() };

    const nId = selectedNode.id;
    const neighborIds = new Set([nId]);
    const neighborLinks = new Set();

    for (const l of graph.links) {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;

      if (s === nId || t === nId) {
        neighborIds.add(s);
        neighborIds.add(t);
        neighborLinks.add(`${s}=>${t}::${l.type}`);
      }
    }

    return { neighborIds, neighborLinks };
  }, [selectedNode, graph.links]);

  const focusNode = (node) => {
    if (!node || !fgRef.current) return;
    try {
      fgRef.current.centerAt(node.x, node.y, 700);
      fgRef.current.zoom(2.0, 700);
    } catch {}
  };

  // ----------------------------
  // D3 Forces
  // ----------------------------
  useEffect(() => {
    if (loading) return;
    const fg = fgRef.current;
    if (!fg) return;

    try {
      const linkForce = fg.d3Force("link");
      const chargeForce = fg.d3Force("charge");

      if (chargeForce) {
        chargeForce.strength(-300);
      }

      if (linkForce) {
        linkForce
          .distance((l) => {
            const t = l.type;
            if (t === "port") return 90;
            if (t === "service") return 100;
            if (t === "tech") return 120;
            if (t === "url") return 140;
            if (t === "threat") return 180;
            if (t === "finding") return 155;
            if (t === "impacted_url") return 160;
            return 135;
          })
          .strength((l) => {
            if (l.type === "finding") return 0.22;
            if (l.type === "impacted_url") return 0.20;
            if (l.type === "threat") return 0.16;
            return 0.12;
          });
      }

      fg.d3Force(
        "collide",
        forceCollide((n) => typeRadius(n) + 18).iterations(2)
      );

      fg.d3AlphaDecay(0.02);
      fg.d3VelocityDecay(0.35);

      fg.d3ReheatSimulation();
    } catch {}
  }, [
    loading,
    graph.nodes.length,
    graph.links.length,
    showPorts,
    showServices,
    showTech,
    showUrls,
    showThreatModels,
    showThreatFindings,
  ]);

  // ----------------------------
  // Cyber canvas node rendering
  // ----------------------------
  const nodeCanvasObject = (node, ctx, globalScale) => {
    const label = node.label || node.id;
    const radius = typeRadius(node);

    const hasSelection = !!selectedNode;
    const isNeighbor = !hasSelection || selectionInfo.neighborIds.has(node.id);
    const isHovered = hoverNode?.id === node.id;

    const baseColor = typeColor(node);
    const fill = isNeighbor ? baseColor : "rgba(120,140,170,0.12)";

    const x = node.x;
    const y = node.y;

    // ✅ Root nodes get a bigger glow + ring so they "pop" hard
    const assetBoost = node.type === TYPE.ASSET ? 1.35 : 1;
    const glow = isNeighbor ? (isHovered ? 34 : 22) * assetBoost : 0;

    ctx.save();

    // glow
    ctx.shadowBlur = glow;
    ctx.shadowColor = isNeighbor ? baseColor : "transparent";
    ctx.fillStyle = fill;

    if (node.type === TYPE.ASSET) {
      // Outer body
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();

      // Outer ring
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "rgba(255,255,255,0.32)";
      ctx.lineWidth = 3.0;
      ctx.beginPath();
      ctx.arc(x, y, radius - 2, 0, 2 * Math.PI);
      ctx.stroke();

      // Inner ring
      ctx.strokeStyle = "rgba(0,210,255,0.22)";
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.arc(x, y, radius - 9, 0, 2 * Math.PI);
      ctx.stroke();

      // Center dot
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      ctx.beginPath();
      ctx.arc(x, y, 3.0, 0, 2 * Math.PI);
      ctx.fill();
    } else if (node.type === TYPE.FINDING) {
      drawDiamond(ctx, x, y, radius);
      ctx.fill();
    } else if (node.type === TYPE.THREAT) {
      drawTriangle(ctx, x, y, radius);
      ctx.fill();
    } else if (node.type === TYPE.URL) {
      drawRoundedSquare(ctx, x, y, radius);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fill();
    }

    // outline
    ctx.shadowBlur = 0;
    ctx.lineWidth = node.type === TYPE.ASSET ? 2.8 : isHovered ? 2.2 : 1.6;
    ctx.strokeStyle = isNeighbor ? "rgba(255,255,255,0.62)" : "rgba(255,255,255,0.10)";

    if (node.type === TYPE.FINDING) {
      drawDiamond(ctx, x, y, radius);
      ctx.stroke();
    } else if (node.type === TYPE.THREAT) {
      drawTriangle(ctx, x, y, radius);
      ctx.stroke();
    } else if (node.type === TYPE.URL) {
      drawRoundedSquare(ctx, x, y, radius);
      ctx.stroke();
    } else if (node.type !== TYPE.ASSET) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // center glyph
    const glyph = typeGlyph(node);
    if (glyph) {
      const glyphSize = Math.max(9, (node.type === TYPE.ASSET ? 18 : 12) / globalScale);

      ctx.font = `900 ${glyphSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      if (node.type === TYPE.FINDING || node.type === TYPE.THREAT || node.type === TYPE.URL) {
        ctx.fillStyle = "rgba(10, 14, 20, 0.92)";
      } else {
        ctx.fillStyle = "rgba(255,255,255,0.92)";
      }

      ctx.fillText(glyph, x, y + 0.5);
    }

    ctx.restore();

    // ----------------------------
    // ✅ LESS TEXT WHEN ZOOMED OUT
    // ----------------------------
    const q = safeStr(search).toLowerCase();
    const matchesSearch = !!q && label.toLowerCase().includes(q);
    const zoomedIn = globalScale > 0.3; // <- you can tune this
    const selectedOrNeighbor = !!selectedNode && selectionInfo.neighborIds.has(node.id);

    const showLabel =
      node.type === TYPE.ASSET ||
      matchesSearch ||
      selectedOrNeighbor ||
      isHovered ||
      (zoomedIn && (node.type === TYPE.URL || node.type === TYPE.THREAT || node.type === TYPE.FINDING));

    if (!showLabel) return;

    // ✅ Smaller when zoomed out, and ASSETS are slightly larger
    const baseFont = node.type === TYPE.ASSET ? 13 : 11;
    const fontSize = Math.max(6, Math.min(16, baseFont * globalScale));
    if (fontSize < 5) return;

    ctx.save();
    ctx.font = `${fontSize}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`;

    const text = label.length > 48 ? label.slice(0, 48) + "…" : label;
    const textWidth = ctx.measureText(text).width;

    const padX = 7;
    const padY = 5;

    const lx = x + radius + 10;
    const ly = y - fontSize / 2 - 2;

    ctx.fillStyle = isNeighbor ? "rgba(5, 11, 20, 0.82)" : "rgba(5, 11, 20, 0.22)";
    ctx.strokeStyle = isNeighbor ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;

    drawRoundRect(ctx, lx - padX, ly - padY, textWidth + padX * 2, fontSize + padY * 2, 7);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = isNeighbor ? "rgba(220, 245, 255, 0.96)" : "rgba(200, 215, 230, 0.28)";
    ctx.fillText(text, lx, ly + fontSize);
    ctx.restore();
  };

  // ----------------------------
  // Edges visible + obvious
  // ----------------------------
  const linkColor = (link) => {
    const s = typeof link.source === "object" ? link.source.id : link.source;
    const t = typeof link.target === "object" ? link.target.id : link.target;

    const hasSelection = !!selectedNode;
    const k = `${s}=>${t}::${link.type}`;
    const isNeighbor = !hasSelection || selectionInfo.neighborLinks.has(k);

    if (!isNeighbor) return "rgba(120,140,170,0.06)";

    if (link.type === "url") return "rgba(0, 190, 110, 0.75)"; // green
    if (link.type === "port") return "rgba(165, 80, 255, 0.65)"; // purple
    if (link.type === "service") return "rgba(165, 80, 255, 0.65)"; // purple
    if (link.type === "tech") return "rgba(165, 80, 255, 0.65)"; // purple
    if (link.type === "threat") return "rgba(255, 150, 35, 0.78)"; // orange

    // TM -> Finding (exploit)
    if (link.type === "finding") return "rgba(255, 60, 90, 0.90)"; // red

    // Finding -> URL
    if (link.type === "impacted_url") return "rgba(255, 60, 90, 0.62)";

    return "rgba(0, 92, 197, 0.40)";
  };

  const linkWidth = (link) => {
    if (link.type === "finding") return 3.0;
    if (link.type === "impacted_url") return 2.4;
    if (link.type === "threat") return 2.3;
    if (link.type === "url") return 2.0;
    return 1.5;
  };

  const linkParticles = (link) => {
    if (link.type === "finding") return 4;
    if (link.type === "impacted_url") return 2;
    return 0;
  };

  // ----------------------------
  // Controls
  // ----------------------------
  const zoomIn = () => {
    if (!fgRef.current) return;
    try {
      const z = fgRef.current.zoom();
      fgRef.current.zoom(z * 1.25, 250);
    } catch {}
  };

  const zoomOut = () => {
    if (!fgRef.current) return;
    try {
      const z = fgRef.current.zoom();
      fgRef.current.zoom(z / 1.25, 250);
    } catch {}
  };

  const fit = () => {
    if (!fgRef.current) return;
    try {
      fgRef.current.zoomToFit(800, 90);
    } catch {}
  };

  const resetSelection = () => setSelectedNode(null);

  // ----------------------------
  // UI
  // ----------------------------
  return (
    <Box sx={{ display: "flex", minHeight: "100vh", flexDirection: "column", bgcolor: "#fff" }}>
      <Nav />

      {/* ✅ tighter page padding so graph gets room */}
      <Box sx={{ flexGrow: 1, p: { xs: 1.5, md: 2 } }}>
        <Stack spacing={1.5}>
          {/* Loading / error */}
          {err ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                borderColor: "rgba(255,80,80,0.35)",
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "rgba(210,40,40,1)" }}>Error</Typography>
              <Typography variant="body2">{err}</Typography>
            </Paper>
          ) : null}

          {/* ✅ Controls + Filters (more compact) */}
          <Card sx={{ p: 1.25, borderRadius: 3, boxShadow: 5 }}>
            <Stack spacing={1.0}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                <Stack spacing={0.2} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HubIcon sx={{ color: BRAND }} />
                    <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                      Attack Graph
                    </Typography>
                    <Chip
                      size="small"
                      label={`Pentest #${pentest_id}`}
                      variant="outlined"
                      sx={{ fontWeight: 900, borderColor: "rgba(0,92,197,0.35)" }}
                    />
                  </Stack>

                  {/* ✅ smaller subtitle */}
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.60)" }}>
                    Assets → Threat Models → Findings → Impacted URLs + Context (ports/services/tech)
                  </Typography>
                </Stack>

                {/* KPIs (small) */}
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                  <Chip size="small" icon={<DnsIcon />} label={`${counts.assets} assets`} sx={{ fontWeight: 900 }} />
                  <Chip size="small" icon={<TerminalIcon />} label={`${counts.tech} tech`} sx={{ fontWeight: 900 }} />
                  <Chip size="small" icon={<LanguageIcon />} label={`${counts.urls} urls`} sx={{ fontWeight: 900 }} />
                  <Chip size="small" icon={<BugReportIcon />} label={`${counts.findings} findings`} sx={{ fontWeight: 900 }} />
                  <Chip size="small" icon={<SecurityIcon />} label={`${counts.threats} TMs`} sx={{ fontWeight: 900 }} />
                  <Chip size="small" icon={<MemoryIcon />} label={`${counts.links} edges`} sx={{ fontWeight: 900 }} />
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.25}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
              >
                {/* Search */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <SearchIcon sx={{ color: BRAND }} />
                  <TextField
                    size="small"
                    placeholder="Search… (asset, url path, finding, tech)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: { xs: "100%", md: 380 } }}
                  />
                  <Button size="small" variant="outlined" onClick={() => setSearch("")}>
                    Clear
                  </Button>
                </Stack>

                {/* Graph controls */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Zoom In">
                    <IconButton size="small" onClick={zoomIn}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Zoom Out">
                    <IconButton size="small" onClick={zoomOut}>
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Fit Graph">
                    <IconButton size="small" onClick={fit}>
                      <CenterFocusStrongIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clear Selection">
                    <IconButton size="small" onClick={resetSelection}>
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Divider />

              {/* Toggle filters (compact row) */}
              <Stack direction={{ xs: "column", md: "row" }} spacing={0.5} flexWrap="wrap">
                <FormControlLabel
                  control={<Switch size="small" checked={showPorts} onChange={(e) => setShowPorts(e.target.checked)} />}
                  label="Ports"
                />
                <FormControlLabel
                  control={
                    <Switch size="small" checked={showServices} onChange={(e) => setShowServices(e.target.checked)} />
                  }
                  label="Services"
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showTech} onChange={(e) => setShowTech(e.target.checked)} />}
                  label="Tech"
                />
                <FormControlLabel
                  control={<Switch size="small" checked={showUrls} onChange={(e) => setShowUrls(e.target.checked)} />}
                  label="URLs"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showThreatModels}
                      onChange={(e) => setShowThreatModels(e.target.checked)}
                    />
                  }
                  label="Threat Models"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showThreatFindings}
                      onChange={(e) => setShowThreatFindings(e.target.checked)}
                    />
                  }
                  label="Findings"
                />
              </Stack>
            </Stack>
          </Card>

          {/* Graph + Details */}
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            {/* Graph */}
            <Card
              sx={{
                flex: 1,
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: 10,
                border: `1px solid rgba(0,92,197,0.22)`,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: { xs: "76vh", md: "84vh" }, // ✅ MUCH BIGGER GRAPH VIEW
                  bgcolor: "#050B14",
                  backgroundImage:
                    "radial-gradient(circle at 18% 20%, rgba(0,92,197,0.15), transparent 55%), radial-gradient(circle at 70% 55%, rgba(0,210,255,0.08), transparent 60%), linear-gradient(rgba(0,92,197,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,92,197,0.05) 1px, transparent 1px)",
                  backgroundSize: "auto, auto, 26px 26px, 26px 26px",
                }}
              >
                {loading ? (
                  <Stack sx={{ position: "absolute", inset: 0 }} alignItems="center" justifyContent="center" spacing={1.25}>
                    <CircularProgress />
                    <Typography sx={{ color: "rgba(220,245,255,0.9)", fontWeight: 900 }}>
                      Building graph…
                    </Typography>
                    <Typography sx={{ color: "rgba(220,245,255,0.65)" }} variant="body2">
                      nodes={graph.nodes.length} • edges={graph.links.length}
                    </Typography>
                  </Stack>
                ) : (
                  <ForceGraph2D
                    ref={fgRef}
                    graphData={graph}
                    nodeId="id"
                    nodeLabel={(n) => `${n.label || n.id}\n${n.type}`}
                    linkColor={linkColor}
                    linkWidth={linkWidth}
                    linkOpacity={0.92}
                    linkDirectionalParticles={linkParticles}
                    linkDirectionalParticleWidth={2}
                    linkDirectionalArrowLength={6}
                    linkDirectionalArrowRelPos={1}
                    linkDirectionalArrowColor={(l) => linkColor(l)}
                    nodeCanvasObject={nodeCanvasObject}
                    onNodeHover={(n) => {
                      setHoverNode(n || null);
                      const canvas = document.querySelector("canvas");
                      if (canvas) canvas.style.cursor = n ? "pointer" : "default";
                    }}
                    onNodeClick={(n) => {
                      setSelectedNode(n);
                      focusNode(n);
                    }}
                    onBackgroundClick={() => setSelectedNode(null)}
                    cooldownTicks={220}
                    d3AlphaDecay={0.02}
                    d3VelocityDecay={0.35}
                    warmupTicks={90}
                  />
                )}
              </Box>
            </Card>

            {/* ✅ Node Inspector ONLY shows when selected (more space for graph) */}
            {selectedNode ? (
              <Card
                sx={{
                  width: { xs: "100%", lg: 360 },
                  borderRadius: 3,
                  boxShadow: 8,
                  border: `1px solid rgba(0,92,197,0.18)`,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Node Inspector</Typography>
                  <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)" }}>
                    Click background to hide this panel.
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ p: 2 }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900 }} noWrap>
                          {selectedNode.label || selectedNode.id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.65)" }}>
                          {selectedNode.type}
                          {selectedNode.severity ? ` • ${normalizeSeverity(selectedNode.severity)}` : ""}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={selectedNode.type.toUpperCase()}
                        sx={{
                          fontWeight: 900,
                          bgcolor: "rgba(0,92,197,0.06)",
                          border: "1px solid rgba(0,92,197,0.25)",
                        }}
                      />
                    </Stack>

                    <Divider />

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        borderColor: "rgba(0,92,197,0.20)",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND }}>
                        Metadata
                      </Typography>

                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          mt: 0.75,
                          fontSize: 12,
                          lineHeight: 1.45,
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {JSON.stringify(selectedNode.meta || {}, null, 2)}
                      </Box>
                    </Paper>

                    <Button
                      variant="contained"
                      onClick={() => focusNode(selectedNode)}
                      sx={{
                        fontWeight: 900,
                        borderRadius: 2,
                        bgcolor: BRAND,
                      }}
                      fullWidth
                    >
                      Focus node
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => setSelectedNode(null)}
                      sx={{ fontWeight: 900, borderRadius: 2 }}
                      fullWidth
                    >
                      Hide inspector
                    </Button>
                  </Stack>
                </Box>
              </Card>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
